import { setBrandTheme, resetBrandTheme } from "../../util/themeService";

export default {
  userTimestamp(state) {
    state.userLoaded = String(Math.round(new Date().getTime() / 1000));
  },
  companyTimestamp(state) {
    state.company.companyLoaded = String(
      Math.round(new Date().getTime() / 1000)
    );
  },
  companyUsersTimestamp(state) {
    state.company.companyUsersLoaded = String(
      Math.round(new Date().getTime() / 1000)
    );
  },
  companyUsersTimestampReset(state) {
    state.company.companyUsersLoaded = null;
  },
  // Loads all user data
  userData(state, payload) {
    state.userId = payload.userId;
    state.usersName = {
      firstName: payload.firstName,
      lastName: payload.lastName,
    };
    state.emailAddress = payload.emailAddress;
    state.userType = payload.userType;
    state.company.companyId = payload.companyId;
    state.profilePicture = payload.profilePicture;
    state.roleId = payload.roleId;
  },
  // Load all user config data
  userConfig(state, payload) {
    state.selectedCulture = payload.selectedCulture;
    state.termsAccepted.date = payload.termsAccepted;
    state.companyTitle = payload.companyTitle;
    state.managerId = payload.managerId;
    state.departments = payload.departments;
  },
  // Load the signed-in user's job preferences. Defensive about a missing
  // payload so an older backend response cannot blank the block out.
  jobPreferences(state, payload) {
    const prefs = payload || {};

    state.jobPreferences = {
      openToWork: Boolean(prefs.openToWork),
      unavailability: prefs.unavailability || [],
      skills: prefs.skills || [],
      location: prefs.location || null,
      roles: prefs.roles || [],
      specialisation: prefs.specialisation || [],
      preferredJobType: prefs.preferredJobType || [],
      preferredLocationType: prefs.preferredLocationType || [],
      workExperience: prefs.workExperience || [],
      references: prefs.references || [],
      // These are rebuilt key by key, so a field the server returns and this
      // list omits is dropped on the way in: the save succeeds and the screen
      // never changes. `qualifications` was exactly that until Work
      // Preferences grew a row for it. Anything added to
      // sanitiseJobPreferences belongs here too.
      qualifications: prefs.qualifications || [],
      industryExperience: prefs.industryExperience || [],
      rightsToWork: {
        type: prefs.rightsToWork?.type || null,
        expiryDate: prefs.rightsToWork?.expiryDate || null,
      },
      salaryRange: {
        min: prefs.salaryRange?.min ?? null,
        max: prefs.salaryRange?.max ?? null,
      },
      capabilityStatement: prefs.capabilityStatement || [],
      summary: prefs.summary || null,
      objective: prefs.objective || null,
      cv: {
        originalName: prefs.cv?.originalName || null,
        uploadedDate: prefs.cv?.uploadedDate || null,
        stored: Boolean(prefs.cv?.stored),
      },
      documentation: prefs.documentation || [],
    };
  },
  theme(state, payload) {
    if (payload) {
      state.theme.navTheme = payload.navTheme;
      state.theme.logoIcon = payload.logoIcon;
      state.theme.logoIconInverse = payload.logoIconInverse;
      state.theme.logoFull = payload.logoFull;
      state.theme.logoFullInverse = payload.logoFullInverse;
      state.theme.styles = payload.styles || {};
      state.theme.buttonStyle = payload.buttonStyle || "pill";
      state.theme.themeEnabled = payload.themeEnabled || false;

      if (payload.themeEnabled && payload.styles) {
        setBrandTheme(payload.styles);
      }
      if (payload.themeEnabled === false) {
        resetBrandTheme();
      }
    }
  },
  // Loads all available companies
  companies(state, payload) {
    state.companies = payload;
  },
  // Loads all company data
  companyData(state, payload) {
    state.company.companyId = payload.companyId;
    state.company.name = payload.companyName;
    state.company.headOfficeAddress = payload.headOfficeAddress;
    state.company.companyDomain = payload.companyDomain;
    state.company.companyType = payload.companyType;
    state.company.allowAddUsers = payload.allowAddUsers;
    state.company.primaryUser = payload.primaryUser;
    state.company.businessNumber = payload.businessNumber;
    state.company.countryCode = payload.countryCode;
  },
  // Loads users that belong to company
  customersInCompany(state, payload) {
    payload.sort((a, b) => a.firstName.localeCompare(b.firstName));
    state.users = payload;
  },
  // Write a single user's job preferences back into the company list.
  //
  // `patchSaveUserConfig` commits to `state.jobPreferences` only when the
  // saved user is the signed-in one, which is right — a recruiter editing a
  // candidate must not overwrite their own profile. But the candidate page
  // and the Marketplace read from `companyUsers`, so without this the change
  // would not appear until that list was refetched.
  companyUserPreferences(state, payload) {
    const user = state.users.find((entry) => entry.userId == payload.userId);

    if (user) {
      user.jobPreferences = payload.jobPreferences;
    }
  },
  // Load all company config data
  companyConfig(state, payload) {
    state.company.registrationComplete = payload.registrationComplete;

    // Only touch what the caller supplied — a commit carrying just
    // registrationComplete must not blank the rest of the config.
    if (payload.features !== undefined) {
      state.company.features = payload.features || {};
    }

    if (payload.recruitment !== undefined) {
      // Two shapes exist in the wild: the schema nests the list under
      // `recruitment.industries`, but records written directly to the database
      // have `recruitment` as the array itself. Accept either so the UI does
      // not silently render empty.
      state.company.recruitment = {
        industries: Array.isArray(payload.recruitment)
          ? payload.recruitment
          : payload.recruitment?.industries || [],
        // Rebuilt key by key like `jobPreferences`, so anything the server
        // sends and this omits is dropped on the way in. The legacy array
        // shape carries no settings at all, which is why it falls back to the
        // schema default rather than to `undefined`.
        capabilityStatementRequired: Array.isArray(payload.recruitment)
          ? false
          : payload.recruitment?.capabilityStatementRequired === true,
        // Defaults to on, as the schema does: only an explicit false is off.
        marketplaceEnabled: Array.isArray(payload.recruitment)
          ? true
          : payload.recruitment?.marketplaceEnabled !== false,
      };
    }
  },
  // Load locations to company data
  companyLocations(state, payload) {
    state.company.locations = payload.locations;
  },
  // Load departments to company data
  companyDepartments(state, payload) {
    state.company.departments = payload.departments;
  },
  // Accepts terms and conditions for user
  acceptTerms(state) {
    state.termsAccepted = {
      accepted: true,
      date: (new Date().getTime() / 1000) | 0,
    };
  },
  // Sets registration to complete for the company
  completedRegistration(state) {
    state.registrationComplete = true;
  },
  // Toggles side menu Lock on and off
  sideMenuLockToggle(state) {
    state.sideMenuLock = !state.sideMenuLock;
  },
  // Toggles banner on and off
  showBannerToggle(state) {
    state.showBanner = !state.showBanner;
  },
  // Set Profile Picture
  profilePicture(state, payload) {
    state.profilePicture = payload;
  },
  // Add permissions to user
  permissions(state, payload) {
    state.permissions = payload;
  },
};
